import { Navigate, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { useMediaQuery } from '../../../lib/use-media-query';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useRoleTemplates } from '../api/jobs-api';
import { groupedTemplates } from '../components/admin-setup/role-template-draft';
import { TemplateEditor } from '../components/admin-setup/TemplateEditor';
import { TemplateLibrary, TemplatePicker } from '../components/admin-setup/TemplateLibrary';
import styles from './RoleTemplatesPage.module.css';

/**
 * ADMIN-05 (F19.05), Role templates A (Admin redesign batch 2): three columns — the library with the approval queue on
 * top, the editor in section tabs, and what a recruiter gets. HR Lead owns the library (E18): departments pick from it,
 * and a department's own template waits in the queue until approved. A job copies a template's setup; editing the
 * template later never changes a job made from it (non-negotiable #4).
 *
 * `/admin/role-templates` opens the first template shown (the queue's first, else the library's); `…/:id` that one, and
 * `…/new` a new one.
 */

/** Laptop width and below: one column, the library a drop-down above the editor. */
const COMPACT_MEDIA = '(max-width: 1180px)';
export const NEW_TEMPLATE = 'new';

export function RoleTemplatesPage(): ReactNode {
  const canView = useCanView('hiring_setup');
  const templates = useRoleTemplates(canView);
  if (!canView) return <ForbiddenState resource="role templates" contact="Your admin" />;
  const { waiting, library } = groupedTemplates(templates.data ?? []);
  const first = waiting[0] ?? library[0];
  if (first) return <Navigate to="/admin/role-templates/$templateId" params={{ templateId: first.id }} replace />;
  return <RoleTemplatesWorkspace selectedId={null} />;
}

export function RoleTemplatesWorkspace({ selectedId }: { readonly selectedId: string | null }): ReactNode {
  const canView = useCanView('hiring_setup');
  const canEdit = useCanEdit('hiring_setup');
  const templates = useRoleTemplates(canView);
  const isCompact = useMediaQuery(COMPACT_MEDIA);
  const navigate = useNavigate();

  if (!canView) return <ForbiddenState resource="role templates" contact="Your admin" />;

  const rows = templates.data ?? [];
  const isNew = selectedId === NEW_TEMPLATE;
  const open = (id: string): void => void navigate({ to: '/admin/role-templates/$templateId', params: { templateId: id } });
  const startNew = (): void => open(NEW_TEMPLATE);
  const existing = rows.find((template) => template.id === selectedId) ?? null;
  const backToList = (): void => void navigate({ to: '/admin/role-templates' });
  const refetch = (): void => void templates.refetch();

  const editor = (): ReactNode => {
    if (templates.isPending) return <SkeletonLines count={10} />;
    if (templates.isError && rows.length === 0) return <ServerErrorState onRetry={refetch} />;
    if (isNew) {
      if (!canEdit) return <ForbiddenState resource="adding role templates" contact="Your admin" isInset />;
      return <TemplateEditor key={NEW_TEMPLATE} latest={null} canEdit isCompact={isCompact} onCreated={(id) => void navigate({ to: '/admin/role-templates/$templateId', params: { templateId: id }, replace: true })} onCancelNew={backToList} onRefetch={refetch} />;
    }
    if (existing) {
      return <TemplateEditor key={existing.id} latest={existing} canEdit={canEdit} isCompact={isCompact} onCreated={open} onCancelNew={backToList} onRefetch={refetch} />;
    }
    if (selectedId !== null) {
      return (
        <EmptyState
          isInset
          title="This template doesn't exist"
          body="It may have been removed, or the link is wrong. Pick a template from the list."
          action={
            <Button variant="outline" size="sm" onClick={backToList}>
              Open the first template
            </Button>
          }
        />
      );
    }
    return (
      <EmptyState
        isInset
        title="No templates yet"
        body="A template fills a job's description, ideal candidate, scoring matrix and pipeline in one click."
        action={
          canEdit ? (
            <Button variant="solid" size="sm" icon={Plus} onClick={startNew}>
              New template
            </Button>
          ) : undefined
        }
      />
    );
  };

  const listedId = isNew ? null : selectedId;
  const list = (): ReactNode => {
    if (templates.isPending) return <SkeletonLines count={6} />;
    if (rows.length === 0) return null;
    return isCompact ? (
      <TemplatePicker templates={rows} selectedId={listedId} onSelect={open} />
    ) : (
      <TemplateLibrary templates={rows} selectedId={listedId} canEdit={canEdit} onSelect={open} />
    );
  };

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>
          Role templates {!templates.isPending && <span className={styles.count}>{rows.length}</span>}
        </h1>
        {canEdit && rows.length > 0 && (
          <Button variant="solid" icon={Plus} disabled={isNew} onClick={startNew}>
            New template
          </Button>
        )}
      </header>
      <p className={styles.lede}>A job copies a template’s setup in one click. Editing a template later never changes a job already made from it.</p>
      <div className={styles.layout} data-has-list={rows.length > 0 || templates.isPending || undefined} data-has-editor={isNew || existing !== null || undefined}>
        {list()}
        {isNew || existing ? editor() : <div className={styles.message}>{editor()}</div>}
      </div>
    </div>
  );
}
